import * as React from "react";
import {Currency} from "../../../../../../config/currencies";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import {LocalOffer} from "@mui/icons-material";
import {MoneyField} from "../../../../../Components/Form/MoneyField";
import {convertAmountToFloat} from "../../../../../../helpers/transaction_helpers";
import {FormModal} from "../../../../../Components/FormModal";
import { Form } from "../../../../../Components/Form/Form/Form";
import { useForm } from "../../../../../../hooks/use_form_v2";
import { DeleteAction } from "./DeleteAction";
import { validateAmountDetailsForm } from "../../../../../../validators/transactions_details_amount_details";
import { TransactionValidationField } from "../../../../../../validators/transactions";
import { sumValues } from "../../../../../../helpers/transaction_details_amount_details";
import { CustomField } from "./CustomField";

interface IAmountDetailsFieldProps {
    currency: Currency;
    amount: string;
    value?: IAmountDetails | null;
    onChange(data: IAmountDetails): void;
}

export interface IAmountDetailsFormState {
    good_price: string;
    shipping_fee: string;
    customs_fee: string;
    other_fee: string;
    custom?: ICustomAmountDetailsFormState[];
}

export interface ICustomAmountDetailsFormState {
    label: string;
    value: string;
}

export interface IAmountDetails {
    goodPrice: number;
    shippingFee: number;
    customsFee: number;
    otherFee: number;
    custom?: ICustomAmountDetails[];
}

export interface ICustomAmountDetails {
    label: string;
    value: number;
}

export const AmountDetailsField = ({currency, amount, value = {} as any, onChange}: IAmountDetailsFieldProps) => {
    const [open, setOpen] = React.useState(false);

    const form = useForm<IAmountDetailsFormState>({
        good_price: value?.goodPrice?.toString() || '',
        shipping_fee: value?.shippingFee?.toString() || '',
        customs_fee: value?.customsFee?.toString() || '',
        other_fee: value?.otherFee?.toString() || '',
        custom: value?.custom?.map(v => {
            return {
                label: v.label,
                value: v.value.toString(),
            };
        }) || [],
    });
    const { change, register, errors, setErrors, getValues } = form;

    const [hasValues, setHasValues] = React.useState<boolean>(sumValues(getValues()) > 0);

    const handleOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
        setErrors(null);
    };

    const handleSubmit = (values: IAmountDetailsFormState) => {
        const validation = validateAmountDetailsForm(values, amount, currency);
        if (!validation.isValid()) {
            return Promise.reject(validation);
        }

        if (sumValues(values) === 0.0) {
            handleClose();
            return Promise.resolve();
        }

        setHasValues(true);

        onChange({
            goodPrice: values.good_price !== '' ? convertAmountToFloat(values.good_price) : null as any,
            shippingFee: values.shipping_fee !== '' ? convertAmountToFloat(values.shipping_fee) : null as any,
            customsFee: values.customs_fee !== '' ? convertAmountToFloat(values.customs_fee) : null as any,
            otherFee: values.other_fee !== '' ? convertAmountToFloat(values.other_fee) : null as any,
            custom: values.custom?.map(v => {
                v.value = convertAmountToFloat(v.value) as any;
                return v;
            }) || null as any,
        });
        handleClose();

        return Promise.resolve();
    };

    const handleDelete = () => {
        setHasValues(false);
        reset();
        onChange(null as any);
        handleClose();
    };

    const reset = () => {
        change()({
            good_price: '',
            shipping_fee: '',
            customs_fee: '',
            other_fee: '',
            custom: [],
        });
    };

    return (
        <>
            <Box sx={{marginLeft: (theme) => theme.spacing(0.5)}}>
                <IconButton size="small" color="primary" onClick={handleOpen} disabled={amount === ''}>
                    <Badge invisible={!hasValues} color="error" variant="dot" overlap="rectangular">
                        <LocalOffer />
                    </Badge>
                </IconButton>
            </Box>

            <FormModal loading={false} open={open} onClose={handleClose} title="Amount details">
                <Form form={form} onSubmit={handleSubmit} errors={errors?.get(TransactionValidationField.AMOUNT_DETAILS_TOTAL)}>
                    <Form.Field type={MoneyField} {...register('good_price')} gridProps={{xs: 12, sm: 6}} />
                    <Form.Field type={MoneyField} {...register('shipping_fee')} gridProps={{xs: 12, sm: 6}} />
                    <Form.Field type={MoneyField} {...register('customs_fee')} gridProps={{xs: 12, sm: 6}} />
                    <Form.Field type={MoneyField} {...register('other_fee')} gridProps={{xs: 12, sm: 6}} />
                    <Form.Field type={CustomField} {...register('custom')} />

                    {!!hasValues && (
                        <Form.Buttons position="afterSubmit" sx={{display: 'inline-block', marginLeft: 2}}>
                            <DeleteAction onDelete={handleDelete} />
                        </Form.Buttons>
                    )}
                </Form>
            </FormModal>
        </>
    );
};
